import { useState } from 'react';
import { message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons/faTrashAlt';
import { getLang } from 'shared/store/selectors/lang.selector';
import { EVENTS, SCHEDULE_EVENT_TYPES, RECURRING_EDIT_SCOPE } from 'shared/constants';
import scheduleApi from 'shared/api/schedule.service.api';
import { useDeleteEventGroup } from 'shared/hooks/schedule-events.hooks';
import { IconButton, Modal } from 'src/components/design-system';
import analyticsService from 'src/helpers/analytics.service';
import monitorService from 'src/helpers/monitor.service';
import { useEducationalEventsContext } from '../../educational-events-context';
import { eventsKeys } from '../../helpers/query-keys';
import { RecurringScopeModal } from '../recurring-scope-modal/recurring-scope-modal';
import styles from './event-detail-panel.module.scss';

export function DeleteEventButton({ event, disabled = false }) {
  const createLang = useSelector(getLang('CREATE_EDUCATIONAL_EVENT'));
  const { setSelectedEventId, refetchEvents } = useEducationalEventsContext();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const deleteGroup = useDeleteEventGroup();
  const isRecurring = !!event.scheduleEventGroupId;

  const afterDelete = async () => {
    setConfirmOpen(false);
    setScopeOpen(false);
    queryClient.removeQueries({ queryKey: eventsKeys.detail(event.id) });
    setSelectedEventId(null);
    await refetchEvents();
  };

  const deleteMutation = useMutation({
    mutationFn: () => scheduleApi.deleteEvent({ eventId: event.id }),
    onSuccess: async () => {
      analyticsService.track(EVENTS.SCHEDULE.DELETE_EVENT_SUBMIT, {
        type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
        eventId: event.id,
        recurring: isRecurring,
        ...(isRecurring && {
          scope: RECURRING_EDIT_SCOPE.SINGLE,
          scheduleEventGroupId: event.scheduleEventGroupId,
        }),
      });
      await afterDelete();
    },
    onError: error => {
      monitorService.error('Failed to delete educational event', { error, eventId: event.id });
      message.error(createLang.DELETE_FAILED);
    },
  });

  const handleDeleteAllFollowing = async () => {
    try {
      await deleteGroup.mutateAsync({ groupId: event.scheduleEventGroupId });
    } catch (error) {
      monitorService.error('Failed to delete recurring event group', { error });
      message.error(createLang.DELETE_FAILED);
      return;
    }
    analyticsService.track(EVENTS.SCHEDULE.DELETE_EVENT_SUBMIT, {
      type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
      eventId: event.id,
      recurring: true,
      scope: RECURRING_EDIT_SCOPE.GROUP,
      scheduleEventGroupId: event.scheduleEventGroupId,
    });
    await afterDelete();
  };

  return (
    <>
      <IconButton
        label={disabled ? createLang.DELETE_DISABLED_CHECK_IN : createLang.DELETE_EVENT}
        className={styles.deleteButton}
        disabled={disabled}
        onClick={() => {
          analyticsService.track(EVENTS.SCHEDULE.DELETE_EVENT_CLICK, {
            type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
            eventId: event.id,
            recurring: isRecurring,
            ...(isRecurring && { scheduleEventGroupId: event.scheduleEventGroupId }),
          });
          if (isRecurring) setScopeOpen(true);
          else setConfirmOpen(true);
        }}>
        <FontAwesomeIcon icon={faTrashAlt} />
      </IconButton>
      {isRecurring ? (
        <RecurringScopeModal
          open={scopeOpen}
          title={createLang.SCOPE_DELETE_TITLE}
          message={createLang.SCOPE_DELETE_MESSAGE}
          lang={createLang}
          danger
          loading={deleteMutation.isPending || deleteGroup.isPending}
          onOnlyThis={() => deleteMutation.mutate()}
          onAllFuture={handleDeleteAllFollowing}
          onCancel={() => setScopeOpen(false)}
        />
      ) : (
        <Modal
          open={confirmOpen}
          onCancel={() => setConfirmOpen(false)}
          onSubmit={() => deleteMutation.mutate()}
          title={createLang.DELETE_CONFIRM_TITLE}
          cancelText={createLang.DELETE_CONFIRM_CANCEL}
          submitText={createLang.DELETE_CONFIRM_CONFIRM}
          submitDanger
          loading={deleteMutation.isPending}
          width={420}>
          <p className={styles.deleteMessage}>{createLang.DELETE_CONFIRM_MESSAGE}</p>
        </Modal>
      )}
    </>
  );
}
